import { GetFunctionFirstParam } from "@/router";
import { GetSdkPromiseType, V2ClientSdk, V2JsonWrapper } from "./V2ClientSdk";
import { StatEventEnum } from "../sdk/ClientSdk";


interface RequestPayload<T> {
    /**
     * 具体操作
     */
    action: string;
    /**
     * 额外参数
     */
    payload: T;
}

/**
 * 工厂选项
 */
interface CreateFactoryOption {
    /**
     * 是否存在回调
     */
    isPromiseCallback: boolean;
}

export class V2BaseClientSdk implements V2ClientSdk {
    private id: number = 0;
    private get requestId() {
        return ++this.id;
    }
    private callbacks: Array<{
        resolve: (payload: any) => void;
        reject: (error: Omit<V2JsonWrapper<string>, 'data'>) => void;
    }> = [];

    /**
     * 最终实现
     * @param payload 
     * @param handler 
     * @returns 
     */
    protected async createFactory<Response, Request extends RequestPayload<any>>(
        payload: Request,
        handler: (jsonStr: string) => void,
        option: CreateFactoryOption
    ) {
        const requestId = this.requestId;
        if (!option.isPromiseCallback) {
            handler(JSON.stringify({
                ...payload,
                requestId,
            }));
            return Promise.resolve((void 0) as Response);
        }
        return new Promise<Response>((resolve, reject) => {
            this.callbacks[requestId] = {
                resolve,
                reject,
            };
            handler(JSON.stringify({
                ...payload,
                requestId,
            }));
            if (!window.webkit.v2message.onMessage) {
                window.webkit.v2message.onMessage = jsonStr => {
                    const response = JSON.parse(jsonStr) as V2JsonWrapper<Response>;
                    // 客户端操作成功(业务操作成功)
                    if (this.callbacks[response.requestId] && response.code == 200) {
                        this.callbacks[response.requestId].resolve(response.data);
                        delete this.callbacks[response.requestId];
                    } else if(this.callbacks[response.requestId]) {
                        this.callbacks[response.requestId].reject(response);
                        delete this.callbacks[requestId];
                    }
                }
            }
        });
    }

    loadV2User() {
        return this.createFactory<GetSdkPromiseType<V2ClientSdk['loadV2User']>, RequestPayload<null>>(
            { action: 'loadV2User', payload: null },
            data => window.webkit.v2message.postMessage(data),
            { isPromiseCallback: true, },
        );
    };

    async goto(payload: GetFunctionFirstParam<V2ClientSdk['goto']>) {
        return this.createFactory<GetSdkPromiseType<V2ClientSdk['goto']>, RequestPayload<GetFunctionFirstParam<V2ClientSdk['goto']>>>(
            {
                action: 'goto',
                payload,
            },
            data => window.webkit.v2message.postMessage(data),
            { isPromiseCallback: false, },
        );
    };
    
    async shared(payload: GetFunctionFirstParam<V2ClientSdk['shared']>) {
        return this.createFactory<GetSdkPromiseType<V2ClientSdk['shared']>, RequestPayload<GetFunctionFirstParam<V2ClientSdk['shared']>>>(
            {
                action: 'shared',
                payload,
            },
            data => window.webkit.v2message.postMessage(data),
            { isPromiseCallback: false, },
        );
    }

    event<R>(eventId: StatEventEnum, params?: R) {
        return this.createFactory<GetSdkPromiseType<V2ClientSdk['event']>, RequestPayload<{
            eventId: StatEventEnum,
            params?: R,
        }>>(
            {
                action: 'event',
                payload: { eventId, params, },
            },
            data => window.webkit.v2message.postMessage(data),
            { isPromiseCallback: false, },
        );
    }
}